import { useCallback, useEffect, useRef, useState } from 'react'
import Autoplay, { AutoplayType } from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'

export default function Importance() {
  const autoplay = useRef<AutoplayType>(
    Autoplay({
      delay: 4500,
      stopOnInteraction: false,
      stopOnMouseEnter: true
    })
  )
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start'
    },
    [autoplay.current]
  )
  const [selectedIndex, setSelectedIndex] = useState(0)
  const benefits = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Conocimiento Práctico',
      description: 'Aprende de experiencias reales en la industria que no encontrarás en los libros de texto. Descubre cómo se aplican los conceptos teóricos en proyectos del mundo real.'
    },
    {
      icon: 'ri-team-line',
      title: 'Networking Profesional',
      description: 'Construye conexiones valiosas con profesionales establecidos en empresas líderes. Estas relaciones pueden abrir puertas a oportunidades laborales futuras.'
    },
    {
      icon: 'ri-compass-line',
      title: 'Orientación de Carrera',
      description: 'Recibe consejos personalizados sobre tu trayectoria profesional, desde la elección de especializaciones hasta estrategias para destacar en entrevistas.'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Ventaja Competitiva',
      description: 'Diferénciate de otros candidatos con insights exclusivos de la industria y una comprensión profunda de las habilidades más demandadas.'
    },
    {
      icon: 'ri-eye-line',
      title: 'Visión de la Industria',
      description: 'Conoce las tendencias actuales, tecnologías emergentes y las habilidades que las empresas realmente buscan en nuevos talentos.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Confianza Profesional',
      description: 'Gana seguridad en tus habilidades y decisiones profesionales al recibir validación y feedback de expertos experimentados.'
    }
  ];

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
    autoplay.current?.reset()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
    autoplay.current?.reset()
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
      autoplay.current?.reset()
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <section id="importancia" className="py-24 bg-gradient-to-br from-[#f8f9fa] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#1b3d70] mb-4">
            La Importancia de las Mentorías
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Las mentorías son el puente entre la formación académica y el éxito profesional. 
            Descubre cómo pueden transformar tu inserción en el mundo laboral.
          </p>
        </div>

        {/* Main Feature */}
        <div className="bg-gradient-to-r from-[#1b3d70] to-[#2a5490] rounded-3xl p-8 sm:p-12 mb-12 text-white">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">
                Acelera tu Inserción Laboral
              </h3>
              <p className="text-lg text-white/90 mb-6 leading-relaxed">
                  ¡Estudiantes con mentoría en México aceleran su inserción laboral: hasta 40% más probabilidades de empleo al graduarte, desarrollando skills técnicas, blandas y adaptándote a la cultura corporativa!.
      
              </p>
              <p className="text-lg text-white/90 mb-6 leading-relaxed">                  En programas universitarios como los de la Leeds School (EEUU), participantes fueron 40% más propensos a tener trabajo al egresar. En contextos similares, mentorías elevan la empleabilidad en 27-40% en primeros meses.
</p>
              <div className="flex items-center gap-4">
                <div className="bg-[#bb8800] rounded-full p-4">
                  <i className="ri-line-chart-line text-3xl"></i>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#bb8800]">40%</div>
                  <div className="text-sm text-white/80">Mayor tasa de empleabilidad</div>
                </div>
              </div>
            </div>
            <div className="relative h-64 sm:h-80">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20mentoring%20session%20illustration%2C%20diverse%20students%20and%20industry%20professionals%20collaborating%2C%20modern%20office%20environment%2C%20warm%20golden%20and%20deep%20blue%20color%20scheme%2C%20inspirational%20atmosphere%2C%20digital%20illustration%20style%2C%20clean%20simple%20background&width=600&height=400&seq=importance-main&orientation=landscape"
                alt="Mentoring Impact"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>

        {/* Benefits Carousel */}
        <div className="relative">
          <div className="embla" ref={emblaRef}>
            <div className="embla__container">
              {benefits.map((benefit, index) => (
                <div className="embla__slide p-2" key={index}>
                  <div 
                    className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#bb8800] transition-all duration-300 hover:shadow-lg group h-full"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-[#bb8800] to-[#996d00] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${benefit.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-[#1b3d70] mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label="Ver beneficio anterior"
                onClick={scrollPrev}
                className="w-12 h-12 rounded-full border border-gray-200 text-[#1b3d70] hover:bg-[#1b3d70] hover:text-white transition-colors duration-300"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <button
                type="button"
                aria-label="Ver siguiente beneficio"
                onClick={scrollNext}
                className="w-12 h-12 rounded-full border border-gray-200 text-[#1b3d70] hover:bg-[#1b3d70] hover:text-white transition-colors duration-300"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-end">
              {benefits.map((_, index) => (
                <button
                  key={`dot-${index}`}
                  type="button"
                  aria-label={`Ir al beneficio ${index + 1}`}
                  onClick={() => scrollTo(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    selectedIndex === index ? 'w-12 bg-[#1b3d70]' : 'w-6 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}

      </div>
    </section>
  );
}